<template>
  <PageTitle @back="onClickBack">Submit Photo (Optional)</PageTitle>
  <div class="content">
    <div class="card">
      <div v-if="!wizardStore.photoData.dataUrl">
        <p class="mb-2">
          Attach a photo to this reading by taking a photo or selecting an existing photo.
        </p>
        <button class="btn btn-primary btn-block" @click="takePhoto">Take Photo</button>
        <button class="btn btn-secondary btn-block" @click="uploadFile">Upload File</button>
      </div>

      <div v-else>
        <p class="font-w600 mb-2">Photo preview</p>
        <div class="photo mb-3">
          <img :src="wizardStore.photoData.dataUrl" alt="photo" />
        </div>

        <button class="btn btn-primary btn-block" @click="takePhoto">Retake</button>
        <button class="btn btn-grey btn-block" @click="onClickClear">Clear</button>
      </div>
    </div>

    <FooterBox @next="onClickNext" />
  </div>
</template>
<script lang="ts" setup>
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import type { Photo } from '@capacitor/camera';
import heic2any from 'heic2any';
import { ref, onMounted } from 'vue';
import type { Ref } from 'vue';
import { useRouter } from 'vue-router';

import { useStore as useWizardStore } from '@/stores/wizard';
import { track } from '@/lib/usage';

import FooterBox from '@/components/FooterBox.vue';
import PageTitle from '@/components/PageTitle.vue';

const emit = defineEmits(['submit', 'back']);

const wizardStore = useWizardStore();
const $router = useRouter();

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to data URL'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
};

const ensurePhotoData = async (photo: Photo): Promise<{ dataUrl: string; format: string }> => {
  let dataUrl = photo.dataUrl || '';
  let format = photo.format || '';
  let sourceBlob: Blob | null = null;
  let mimeType = '';

  if (!dataUrl || dataUrl.startsWith('blob:')) {
    const path = photo.webPath || photo.path;
    if (path) {
      const response = await fetch(path);
      sourceBlob = await response.blob();
      mimeType = sourceBlob.type || '';
      dataUrl = await blobToDataUrl(sourceBlob);
    }
  } else {
    const match = dataUrl.match(/^data:(.*?);/);
    mimeType = match ? match[1] : '';
  }

  if (!format && mimeType) {
    const subtype = mimeType.split('/')[1] || '';
    format = subtype;
  }

  const isHeic =
    format === 'heic' ||
    format === 'heif' ||
    mimeType === 'image/heic' ||
    mimeType === 'image/heif';

  if (isHeic) {
    let heicBlob = sourceBlob;
    if (!heicBlob) {
      const fetchPath = photo.webPath || photo.path || dataUrl;
      if (!fetchPath) {
        throw new Error('No valid path or dataUrl available to fetch HEIC blob');
      }
      const response = await fetch(fetchPath);
      heicBlob = await response.blob();
    }

    if (!heicBlob) {
      throw new Error('Could not obtain HEIC blob for conversion');
    }

    const conversionResult = await heic2any({ blob: heicBlob, toType: 'image/jpeg', quality: 0.9 });

    const jpegBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
    const jpegDataUrl = await blobToDataUrl(jpegBlob as Blob);
    return {
      dataUrl: jpegDataUrl,
      format: 'jpeg'
    };
  }

  return {
    dataUrl,
    format
  };
};

const photo: Ref<Photo | null> = ref(null);

onMounted(() => {
  window.scrollTo(0, 0);
});

const takePhoto = async () => {
  await handleImageResult(
    Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl
    }),
    'camera'
  );
};

const uploadFile = async () => {
  await handleImageResult(getFilePickerImageWithFallback(), 'file');
};

const handleImageResult = async (photoPromise: Promise<Photo>, source: 'camera' | 'file') => {
  try {
    const result = await photoPromise;

    const { dataUrl, format } = await ensurePhotoData(result);

    if (dataUrl) {
      wizardStore.setPhotoData({
        format,
        dataUrl
      });
      track('reading_photo_captured', { source });
    }
  } catch (error: unknown) {
    if (error instanceof Error && error.message === 'User cancelled photos app') {
      return;
    }
    console.error('Error capturing photo:', error);
  }
};

// Try to get image from device file picker if available, otherwise fall back to file explorer
const getFilePickerImageWithFallback = async (): Promise<Photo> => {
  const baseOptions = {
    quality: 90,
    allowEditing: true,
    resultType: CameraResultType.DataUrl
  };

  let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

  try {
    const timeoutPromise = new Promise<never>((resolve, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Prompt timeout')), 800);
    });

    const result = await Promise.race([
      Camera.getPhoto({
        ...baseOptions,
        source: CameraSource.Prompt
      }),
      timeoutPromise
    ]);

    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    return await Camera.getPhoto({
      ...baseOptions,
      source: CameraSource.Photos
    });
  }
};

const onClickClear = () => {
  wizardStore.setPhotoData({
    dataUrl: '',
    format: ''
  });

  photo.value = null;
};

const onClickNext = async () => {
  $router.push({ name: 'confirmation' });
};

const onClickBack = () => {
  $router.push({ name: 'reading' });
};
</script>
<style>
.photo {
  width: 100%;
  height: auto;
}
.photo img {
  width: 100%;
  max-height: 350px;
  object-fit: contain;
}
</style>
